import React, { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { 
    Plus, 
    Trash2, 
    GripVertical, 
    Edit2, 
    Eye,
    ChevronDown,
    ChevronUp,
    Check,
    ChevronsUpDown,
    AlertCircle,
    Underline,
    Italic,
    Bold,
    AlertCircleIcon
} from 'lucide-react';
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { toast } from 'sonner';
import type { PropertyListings } from '@/types/PropertyListings';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
  } from "@/components/ui/command"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
  } from "@/components/ui/popover"
import { cn } from '@/lib/utils';
import Helper from '@/lib/helper';
import api from '@/lib/axiosInstance';
import { signingPortalPayload } from '@/lib/signingPortal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import { useSearchParams } from 'react-router-dom';
import SigningDetailsDialog from '@/components/documents/SigningDetailsDialog';
import { applyClauseStamp, type StampOrigin } from '@/components/documents/stamp';

type SignatoryType = 'vendor' | 'purchaser' | 'tenant' | 'landlord';

type SignatureFields = {
    name?: boolean;
    nric?: boolean;
    address?: boolean;
    date?: boolean;
    stamp?: boolean;
}

type Field = { id: string; name: string };

interface Clause {
    id: string;
    title: string;
    content: string;
    fields?: Field[];
    editable: boolean;
    isList?: boolean;
    listDisplay?: 'horizontal' | 'vertical';
    listItems?: { 
        id?: string;
        label: string; 
        description: string; 
        fields?: Field[] 
    }[];
    isSignature?: boolean;
    signatories?: Array<{
        type: SignatoryType;
        fields: SignatureFields;
    }>;
    needsWitness?: boolean;
    needStamp?: boolean;
    witnessFields?: SignatureFields;
}

interface Section {
    id: string;
    title: string;
    editable: boolean;
    pretext?: string;
    pretextSeparator?: boolean;
    clauses: Clause[];
    collapsed?: boolean;
}

interface ClauseItemProps {
    clause: Clause;
    sectionId: string;
    index: number;
    updateClauseTitle: (sectionId: string, clauseId: string, title: string) => void;
    updateClauseContent: (sectionId: string, clauseId: string, content: string) => void;
    deleteClause: (sectionId: string, clauseId: string) => void;
    onEditClause: (sectionId: string, clause: Clause) => void;
    selectedListing: PropertyListings | null;
    documentType: string;
}

const ClauseItem: React.FC<ClauseItemProps> = ({
    clause,
    sectionId,
    index,
    updateClauseTitle,
    updateClauseContent,
    deleteClause,
    onEditClause,
    selectedListing,
    documentType
}) => {
    const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
    const [tempTitle, setTempTitle] = useState<string>(clause.title);

    return (
        <div className="border rounded-lg p-4 mb-4 bg-white">
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2 flex-1">
                    <GripVertical className="h-4 w-4 text-gray-600 cursor-move" />
                    {
                        isEditingTitle ? (
                            <div className="flex-1 flex gap-2">
                                <Input
                                value={tempTitle}
                                onChange={(e) => setTempTitle(e.target.value)}
                                className="text-sm font-semibold"/>
                            </div>
                        ) : (
                            <h4 className="font-semibold text-sm flex-1">{clause.title}</h4>
                        )
                    }
                </div>
                {/* {
                    selectedListing && ( */}
                        <div className="flex gap-1">
                        {
                            isEditingTitle ? 
                                <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                    updateClauseTitle(sectionId, clause.id, tempTitle);
                                    setIsEditingTitle(false);
                                }}>
                                    <Check className="h-4 w-4" />
                                </Button>
                                :
                                <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setIsEditingTitle(!isEditingTitle)}>
                                    <Edit2 className="h-4 w-4" />
                                </Button>
                            }
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => deleteClause(sectionId, clause.id)}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                        </div>
                    {/* )
                } */}
            </div>
            <div className="mb-3">
                <div className="relative group">
                    <p
                    className="text-sm text-gray-700 whitespace-normal"
                    dangerouslySetInnerHTML={{ __html: clause.content }}/>
                </div>
                {
                    clause.isList && (
                        <div className="mb-3">
                            {
                                clause.listItems && clause.listItems?.length > 0 && (
                                    <div className="mt-2">
                                        {
                                            clause.listItems.map((item, idx) => (
                                                <div
                                                key={idx}
                                                className={
                                                    clause.listDisplay === "horizontal"
                                                    ? "grid grid-cols-12 items-start gap-x-3 mb-2 text-sm"
                                                    : "flex items-start gap-2 mb-2 text-sm"
                                                }>
                                                {
                                                    clause.listDisplay === "horizontal" ? (
                                                        <>
                                                            <div className='col-span-1 font-semibold pr-2'>{idx + 1}.</div>
                                                            {
                                                                item.label === '' ? 
                                                                <>
                                                                    <div className="col-span-11 text-gray-700 whitespace-normal"
                                                                    dangerouslySetInnerHTML={{ __html: item.description }}/>
                                                                </>
                                                                :
                                                                item.description === '' ?
                                                                <>
                                                                    <div className="col-span-11 font-semibold pr-2">
                                                                        {item.label}
                                                                    </div>
                                                                </>
                                                                : 
                                                                <>
                                                                    <div className="col-span-2 font-semibold pr-2">
                                                                        {item.label}
                                                                    </div>
                                                                    <div className="col-span-9 text-gray-700 whitespace-normal"
                                                                    dangerouslySetInnerHTML={{ __html: item.description }}/>
                                                                </>
                                                            }
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className="font-semibold">{idx + 1}.</span>
                                                            <div className="flex-1">
                                                                <p className="font-semibold italic text-sm">{item.label}</p>
                                                                <p 
                                                                className="text-gray-700 text-sm whitespace-normal" 
                                                                dangerouslySetInnerHTML={{ __html: item.description }}/>
                                                            </div>
                                                        </>
                                                    )
                                                }
                                                </div>
                                            ))
                                        }
                                    </div>
    
                                )
                            }
                        </div>
                    )
                }
            </div>
            {
                clause.isSignature && clause.signatories && clause.signatories.length > 0 && (
                    <div className="mb-5">
                        <h5 className="font-semibold text-sm mb-2">{clause.title}</h5>
                        <div className={`grid gap-4 mb-4 grid-cols-2`}>
                            <div className="space-y-4">
                                {clause.signatories.map((signatory, idx) => {
                                    const signatoryLabel = signatory.type === 'vendor' 
                                        ? (clause.signatories!.filter(s => s.type === 'vendor').length > 1 
                                            ? `VENDOR ${clause.signatories!.filter((s, i) => s.type === 'vendor' && i <= idx).length}` 
                                            : 'VENDOR')
                                        : (clause.signatories!.filter(s => s.type === 'purchaser').length > 1 
                                            ? `PURCHASER ${clause.signatories!.filter((s, i) => s.type === 'purchaser' && i <= idx).length}` 
                                            : 'PURCHASER');

                                    return (
                                        <div key={idx} className="rounded-lg p-3">
                                            <div className="text-gray-800 font-semibold text-xs mb-2">{signatoryLabel}</div>
                                            <div className="text-gray-600 text-xs mb-1">Sign here</div>
                                            <div className="border border-gray-300 rounded-md h-[120px] mb-3 flex justify-center items-center">
                                                <code className='bg-gray-200 px-1 py-0.5 rounded text-xs'>
                                                    {`_____${signatory.type.toUpperCase()}_${idx + 1}_SIGNATURE_____`}
                                                </code>
                                            </div>
                                            <div className="space-y-2 text-sm mt-5">
                                                {signatory.fields.name && (
                                                    <div className="flex items-center">
                                                        <span className="text-gray-600 text-xs">Name:</span>
                                                        <code className='bg-gray-200 px-1 py-0.5 rounded text-xs ml-2'>
                                                            {`__${signatory.type.toUpperCase()}_${idx + 1}_NAME__`}
                                                        </code>
                                                    </div>
                                                )}
                                                {signatory.fields.nric && (
                                                    <div className="flex items-center">
                                                        <span className="text-gray-600 text-xs">NRIC:</span>
                                                        <code className='bg-gray-200 px-1 py-0.5 rounded text-xs ml-2'>
                                                            {`__${signatory.type.toUpperCase()}_${idx + 1}_NRIC__`}
                                                        </code>
                                                    </div>
                                                )}
                                                {signatory.fields.address && (
                                                    <div className="flex items-center">
                                                        <span className="text-gray-600 text-xs">Address:</span>
                                                        <code className='bg-gray-200 px-1 py-0.5 rounded text-xs ml-2'>
                                                            {`_____${signatory.type.toUpperCase()}_${idx + 1}_ADDRESS_____`}
                                                        </code>
                                                    </div>
                                                )}
                                                {signatory.fields.date && (
                                                    <div className="flex items-center">
                                                        <span className="text-gray-600 text-xs">Date:</span>
                                                        <code className='bg-gray-200 px-1 py-0.5 rounded text-xs ml-2'>
                                                            {`__${signatory.type.toUpperCase()}_${idx + 1}_DATE__`}
                                                        </code>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                {
                                    clause.needStamp && (
                                        <div className="rounded-lg p-3">
                                            <div className="space-y-2 text-sm">
                                                <div>
                                                    <span className="text-gray-600 text-xs">Company Stamp:</span>
                                                    <div className='border border-gray-300 rounded-md h-[120px] flex items-center justify-center'>
                                                        <code className='bg-gray-200 px-1 py-0.5 rounded text-xs ml-2'>
                                                            {`__COMPANY_STAMP__`}
                                                        </code>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                }
                            </div>
                            {clause.needsWitness && (
                                <div className="rounded-lg p-3">
                                    <div className="text-gray-800 font-semibold text-xs mb-2">WITNESS</div>
                                    <div className="text-gray-600 text-xs mb-1">Witness signature</div>
                                    <div className="border border-gray-300 rounded-md h-[120px] mb-3 flex justify-center items-center bg-white">
                                        <code className='bg-gray-200 px-1 py-0.5 rounded text-xs'>
                                            _____WITNESS_SIGNATURE_____
                                        </code>
                                    </div>
                                    <div className="space-y-2 text-sm mt-5">
                                        {clause.witnessFields?.name && (
                                            <div className="flex items-center">
                                                <span className="text-gray-600 text-xs">Name:</span>
                                                <code className='bg-gray-200 px-1 py-0.5 rounded text-xs ml-2'>
                                                    __WITNESS_NAME__
                                                </code>
                                            </div>
                                        )}
                                        {clause.witnessFields?.nric && (
                                            <div className="flex items-center">
                                                <span className="text-gray-600 text-xs">NRIC:</span>
                                                <code className='bg-gray-200 px-1 py-0.5 rounded text-xs ml-2'>
                                                    __WITNESS_NRIC__
                                                </code>
                                            </div>
                                        )}
                                        {clause.witnessFields?.address && (
                                            <div className="flex items-center">
                                                <span className="text-gray-600 text-xs">Address:</span>
                                                <code className='bg-gray-200 px-1 py-0.5 rounded text-xs ml-2'>
                                                    _____WITNESS_ADDRESS_____
                                                </code>
                                            </div>
                                        )}
                                        {clause.witnessFields?.date && (
                                            <div className="flex items-center">
                                                <span className="text-gray-600 text-xs">Date:</span>
                                                <code className='bg-gray-200 px-1 py-0.5 rounded text-xs ml-2'>
                                                    __WITNESS_DATE__
                                                </code>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )
            }
            {/* {
                selectedListing && ( */}
                    <div className="mt-3 flex justify-end">
                        <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEditClause(sectionId, clause)}>
                            Edit Clause
                        </Button>
                    </div>
                {/* )
            } */}
        </div>
    );
};

type FormField = {
    label: string;
    variable: string;
    required: boolean;
    value: string | number;
};

const ExclusiveAuthorizationToRent = () => {
    const [searchParams] = useSearchParams();
    const [documentTitle, setDocumentTitle] = useState('EXCLUSIVE AUTHORIZATION TO RENT');
    const [editingTitle, setEditingTitle] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [selectedListing, setSelectedListing] = useState<PropertyListings | null>(null);
    const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
    const [listings, setListings] = useState<PropertyListings[] | null>(null);
    const [showPreview, setShowPreview] = useState<boolean>(false);
    const [popSearchQuery, setPopSearchQuery] = useState<string>('');
    const [fieldDialogOpen, setFieldDialogOpen] = useState<boolean>(false);
    const editorRef = useRef<HTMLDivElement>(null);
    const listItemEditorsRef = useRef<(HTMLDivElement | null)[]>([]);
    const activeFormatsRef = useRef({ bold: false, italic: false, underline: false });
    const boldBtnRef = useRef<HTMLButtonElement>(null);
    const italicBtnRef = useRef<HTMLButtonElement>(null);
    const underlineBtnRef = useRef<HTMLButtonElement>(null);
    const [submitDialogOpen, setSubmitDialogOpen] = useState<boolean>(false);
    const currentListItemIndex = useRef<number>(0);
    const [loading, setloading] = useState<boolean>(false);
    const [signingMode, setSigningMode] = useState<'docusign' | 'local'>('local');
    const [documentType, setDocumentType] = useState<'individual' | 'company'>('company');
    const [generatingPdf, setGeneratingPdf] = useState<boolean>(false);
    const form = useRef<FormField[]>([
        {
            label: 'Appointment Period',
            variable: 'appointmentPeriod',
            required: true,
            value: 1
        },
        {
            label: 'Appointment Commencement Date',
            variable: 'appointmentCommencementDate',
            required: true,
            value: ''
        },
        {
            label: 'Appointment Expiring Date',
            variable: 'appointmentExpiringDate',
            required: true,
            value: ''
        },
        {
            label: 'Security Deposit (Months)',
            variable: 'securityDepositMonths',
            required: true,
            value: 2
        },
        {
            label: 'Security Deposit Amount',
            variable: 'securityDepositAmount',
            required: false,
            value: ''
        },
        {
            label: 'Utility Deposit (Months)',
            variable: 'utilityDepositMonths',
            required: true,
            value: 1
        },
        {
            label: 'Utility Deposit Amount',
            variable: 'utilityDepositAmount',
            required: false,
            value: ''
        },
        {
            label: 'Advance Rental (Months)',
            variable: 'advanceRentalMonths',
            required: true,
            value: 1
        },
        {
            label: 'Advance Rental Amount',
            variable: 'advanceRentalAmount',
            required: false,
            value: ''
        },
        {
            label: 'Rental Fee (Months)',
            variable: 'rentalFeeMonths',
            required: true,
            value: 1
        }
    ]);

    const fillPaymentTemplate = (template: string, values: Record<string, any>) => {
        if (!selectedListing) return template;
    
        let result = template;
        const amountFields = [
            'rentalFeeMonths',
            'utilityDepositMonths',
            'advanceRentalMonths'
        ];
        
        amountFields.forEach((field) => {
            const formField = form.current.find((f) => f.variable === field);
            if (formField) {
                const months = Number(formField.value || 0);
                const amount = selectedListing.price * months;
                const words = Helper.convertNumberToWords(amount);
                result = result.replace("RMXXXX", `RM${Helper.formatWithDecimal(amount)} (${words})`);
            }
        });
    
        return result;
    };
    
    
    const inputHtml = ({
        field,
        disabled
    }: {
        field: string;
        disabled?: boolean
    }) => {
        return `<input
                type="text"
                class="w-21 border rounded px-1"
                value="{{${field}}}"
                id="${field}"
                data-field="${field}"
                disabled/>`
    }

    const APPOINTMENT_PERIOD = `${inputHtml({field: 'appointmentPeriod' })} months commencing from ${inputHtml({field: 'appointmentCommencementDate'})} and expiring ${inputHtml({field: 'appointmentExpiringDate'})},
 and can be renewed for another period as agreed upon by both parties.`;
    const PAYMENT_TEMPLATE = `a) Security Deposit of RMXXXX or equivalent to ${inputHtml({field: 'rentalFeeMonths'})} month's Rental, and or<br/>
    b) Utility Deposit of RMXXXX or equivalent to ${inputHtml({field: 'utilityDepositMonths'})} month's Rental, and or<br/>
    c) Advance Rental of RMXXXX or equivalent to ${inputHtml({field: 'advanceRentalMonths'})} month's Rental`;
    const FEE_TEMPLATE = `I/We hereby agree to pay professional fee equivalent to ${inputHtml({field: 'advanceRentalMonths'})} months(s) of rental plus 8% SST ("Professional Fees"), payable upon execution
    of the Tenancy Agreement ("TA") for the rental of the Property. You are entitled to your Professional Fees if any introduction made during the
    appointment leads to successfully concluded tenancy even if the appointment has expired or has been terminated at the time of completion of
    the transaction.`;
    
    const fillTemplate = (template: string, values: Record<string, any>) => {
        return template.replace(/{{(.*?)}}/g, (_, key) => {
            return values[key] !== undefined ? String(values[key]) : "";
        });
    };
    
    const replaceInputsWithValues = (
        html: string,
        formValues: Array<{ variable: string; value: any }>
    ) => {
        const valueMap = formValues.reduce<Record<string, any>>((acc, item) => {
            acc[item.variable] = item.value;
            return acc;
        }, {});

        return html?.replace(
            /<input[^>]*data-field="([^"]+)"[^>]*>/g,
            (_, fieldName) => {
                const value = valueMap[fieldName];
                return value != null ? `<strong><u>${String(value)}</u></strong>` : '';
            }
        );
    };

    const toolbarRef = useRef<HTMLDivElement>(null);
    const singleToolbarRef = useRef<HTMLDivElement>(null);
    const dialogContentRef = useRef<HTMLDivElement>(null);
    const mousePositionRef = useRef<{ 
        x: number; 
        y: number;
        maxX: number;
        maxY: number;
    }>({
        x: 0,
        y: 0,
        maxX: 0,
        maxY: 0
    });
    const listItemBoldBtnRef = useRef<HTMLButtonElement>(null);
    const listItemItalicBtnRef = useRef<HTMLButtonElement>(null);
    const listItemUnderlineBtnRef = useRef<HTMLButtonElement>(null);
    const [sections, setSections] = useState<Section[]>([
        {
            id: 'property',
            title: '',
            editable: true,
            clauses: [
                {
                    id: 'propertyAddress',
                    title: '',
                    content: `I/We <code class="bg-gray-200 px-1 py-0.5 rounded text-xs">_______${documentType === 'individual' ? 'OWNER' : 'COMPANY'}_NAME_______</code> ${documentType === 'individual' ? 'NRIC': 'Company Registered No'}. <code class="bg-gray-200 px-1 py-0.5 rounded text-xs">_____${documentType === 'individual' ? 'NRIC' : 'COMPANY_REG_NO'}_____</code>, being
the registered owner/person authorised by the registered owner ("Vendor ") for the property bearing the postal address known as
<code class="bg-gray-200 px-1 py-0.5 rounded text-xs">__________________${documentType === 'individual' ? 'ADDRESS' : 'COMPANY_ADDRESS'}__________________</code> (" Property") hereby irrevocably appoint <strong>Salt & Light Realty Sdn Bhd
(“SLRSB”)</strong> to secure a Tenant for the Property on the following terms and conditions:-`,
                    editable: true
                },
                {
                    id: 'mainContent',
                    title: '',
                    content: '',
                    editable: true,
                    isList: true,
                    listDisplay: 'horizontal',
                    listItems: [
                        {
                            label: 'Appointment Basis: ',
                            description: 'Sole/Joint/Ad-hoc Agency*',
                        },
                        {
                            id: 'appointmentPeriod',
                            label: 'Appointment Period: ',
                            description: 
                            `____________( ) months commencing from ______________ and expiring ____________,
 and can be renewed for another period as agreed upon by both parties.`
                        },
                        {
                            id: 'sellingPrice',
                            label: 'Asking Monthly Rental: ',
                            description: `RM _________________ (Ringgit Malaysia_________________________________________________) <br>Inclusive the nearest offer acceptable by me/us as the Vendor`
                        },
                        {
                            id: 'paymentTerms',
                            label: 'Payment Terms: ',
                            description: `a) Security Deposit of RM<code>__MONTH_AMOUNT__</code> or equivalent to <code>__MONTH__</code>month's Rental, and or<br/>
b) Utility Deposit of RM<code>__MONTH_AMOUNT__</code> or equivalent to <code>__MONTH__</code>month's Rental, and or<br/>
c) Advance Rental of RM<code>__MONTH_AMOUNT__</code> or equivalent to <code>__MONTH__</code>month's Rental`
                        },
                        {
                            label: 'Furnishing: ',
                            description: `The Property is let out unfurnished/partial furnished/fully furnished* - see attached Inventory List, if any`
                        },
                        {
                            id: 'professionalFees',
                            label: '',
                            description: `I/We hereby agree to pay professional fee equivalent to months(s) of rental plus 8% SST ("Professional Fees"), payable upon execution
of the Tenancy Agreement ("TA") for the rental of the Property. You are entitled to your Professional Fees if any introduction made during the
appointment leads to successfully concluded tenancy even if the appointment has expired or has been terminated at the time of completion of
the transaction.`,
                        },
                        {
                            label: '',
                            description: `You are hereby authorised to display a "TO LET/FOR RENT" signboard I advertise in the media or Internet I send out flyers and promote
generally the sale of the property based on the above-mentioned terms and conditions.`
                        },
                        {
                            label: '',
                            description: `You are hereby authorised to collect and accept from the Tenant on my/our behalf an Earnest Deposit equivalent to month's Rental or any sum subsequently agreed by Landlord and to safekeep the said Earnest Deposit as a stakeholder.`
                        },
                        {
                            label: '',
                            description: `You are hereby authorised to deduct your Professional Fees from the Earnest Deposit before releasing the balance (if any) to the Landlord. In
the event, the Earnest Deposit is insufficient to pay SLRSB’s full Professional Fees, I/we hereby undertake and agree to make good the shortfall immediately.`
                        },
                        {
                            label: '',
                            description: `In the event the transaction is aborted by either the Landlord or the Tenant before the TA is signed but initial forfeitable Earnest Deposit is paid,
I/we shall :-<br>
a) pay <strong>SLRSB</strong> a professional fee equivalent to 50% of the full Professional Fees or 50% of the initial forfeitable Earnest Deposit, whichever
is lesser; and<br>
b) fully indemnify <strong>SLRSB</strong> against all losses, damages or claims made by the Tenant(s).`
                        },
                        {
                            label: '',
                            description: `I/We hereby warrant and affirm that this appointment shall be binding on my/our nominees, heirs, representatives, assignees and successors
in title.`
                        },
                        {
                            label: '',
                            description: `his appointment is given in my/our capacity as (please tick where is applicable):<br>
- the registered proprietor/s - enclosed Document of Title/S PA Latest Quit Rent Bill/Latest Assessment Bill<br>
- duly authorised representatives pursuant to a Power of Attorney dated ___/___/20___ (dd/mm/yyyy) and registered at the High Court registration No. __________________________________<br>
- trustee for the legal owner who is/are _____________________________________________`
                        },
                    ]
                },
                {
                    id: 'vendorSign',
                    title: '',
                    content: '',
                    isSignature: true,
                    signatories: [
                        {
                            type: 'vendor',
                            fields: {
                                name: true,
                                nric: true,
                                address: true,
                                date: true,
                                stamp: true
                            }
                        }
                    ],
                    needsWitness: true,
                    needStamp: true,
                    witnessFields: {
                        name: true,
                        nric: true,
                        address: false,
                        date: true
                    },
                    editable: true
                }
            ]
        },
    ]); 
    const [showAddClauseDialog, setShowAddClauseDialog] = useState(false);
    const [currentSectionId, setCurrentSectionId] = useState<string>('');
    const [newClause, setNewClause] = useState<Clause>({
        id: '',
        title: '',
        content: '',
        editable: true
    });
    const editorContentRef = useRef(newClause.content);
    const [editingClause, setEditingClause] = useState<{ sectionId: string; clauseId: string } | null>(null);
    const [editorsInitialized, setEditorsInitialized] = useState(false);
    const [signatoryDetails, setSignatoryDetails] = useState<Array<{
        type: 'vendor' | 'purchaser' | 'witness';
        name: string;
        email: string;
        fields: SignatureFields;
        label: string;
        origin?: StampOrigin;
    }>>([]);

    const detectFormats = () => {
        const formats = {
            bold: document.queryCommandState('bold'),
            italic: document.queryCommandState('italic'),
            underline: document.queryCommandState('underline'),
        };

        activeFormatsRef.current = formats;

        if (boldBtnRef.current) {
            boldBtnRef.current.style.backgroundColor = formats.bold ? 'black' : 'transparent';
            boldBtnRef.current.style.color = formats.bold ? 'white' : 'black';
        }
        if (italicBtnRef.current) {
            italicBtnRef.current.style.backgroundColor = formats.italic ? 'black' : 'transparent';
            italicBtnRef.current.style.color = formats.italic ? 'white' : 'black';
        }
        if (underlineBtnRef.current) {
            underlineBtnRef.current.style.backgroundColor = formats.underline ? 'black' : 'transparent';
            underlineBtnRef.current.style.color = formats.underline ? 'white' : 'black';
        }
    };

    const detectListItemFormats = (idx?: number) => {
        const editorIdx = idx ?? currentListItemIndex.current;
        if (editorIdx === null || editorIdx === undefined) return;
        const editor = listItemEditorsRef.current[editorIdx];
        if (!editor) return;

        const formats = {
            bold: document.queryCommandState('bold'),
            italic: document.queryCommandState('italic'),
            underline: document.queryCommandState('underline'),
        };

        if (listItemBoldBtnRef.current) {
            listItemBoldBtnRef.current.style.backgroundColor = formats.bold ? 'black' : 'transparent';
            listItemBoldBtnRef.current.style.color = formats.bold ? 'white' : 'black';
        }
        if (listItemItalicBtnRef.current) {
            listItemItalicBtnRef.current.style.backgroundColor = formats.italic ? 'black' : 'transparent';
            listItemItalicBtnRef.current.style.color = formats.italic ? 'white' : 'black';
        }
        if (listItemUnderlineBtnRef.current) {
            listItemUnderlineBtnRef.current.style.backgroundColor = formats.underline ? 'black' : 'transparent';
            listItemUnderlineBtnRef.current.style.color = formats.underline ? 'white' : 'black';
        }
    };

    const applyFormat = (tag: 'strong' | 'em' | 'u') => {
        const editor = editorRef.current;
        if (!editor) return;

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        const range = selection.getRangeAt(0);
        if (!editor.contains(range.startContainer) || !editor.contains(range.endContainer)) return;

        const cmd = tag === 'strong' ? 'bold' : tag === 'em' ? 'italic' : 'underline';

        if (selection.isCollapsed) {
            document.execCommand(cmd);
            editorContentRef.current = editor.innerHTML;
            detectFormats();
            return;
        }

        let succeeded = false;
        try { succeeded = document.execCommand(cmd); } catch {}
        if (succeeded) {
            editorContentRef.current = editor.innerHTML;
            detectFormats();
            return;
        }

        const extracted = range.extractContents();
        const wrapper = document.createElement(tag);
        wrapper.appendChild(extracted);
        range.insertNode(wrapper);

        selection.removeAllRanges();
        const newRange = document.createRange();
        newRange.setStartAfter(wrapper);
        newRange.collapse(true);
        selection.addRange(newRange);

        editorContentRef.current = editor.innerHTML;
        detectFormats();
    };

    const applyFormatToListItem = (tag: 'strong' | 'em' | 'u', editor: HTMLDivElement | null) => {
        if (!editor) return;

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        const range = selection.getRangeAt(0);
        if (!editor.contains(range.startContainer) || !editor.contains(range.endContainer)) return;

        const cmd = tag === 'strong' ? 'bold' : tag === 'em' ? 'italic' : 'underline';

        if (selection.isCollapsed) {
            document.execCommand(cmd);
            updateEditorHtmlFromRef(editor);
            detectListItemFormats();
            return;
        }

        let succeeded = false;
        try { succeeded = document.execCommand(cmd); } catch {}
        if (succeeded) {
            updateEditorHtmlFromRef(editor);
            detectListItemFormats();
            return;
        }

        const extracted = range.extractContents();
        const wrapper = document.createElement(tag);
        wrapper.appendChild(extracted);
        range.insertNode(wrapper);

        selection.removeAllRanges();
        const newRange = document.createRange();
        newRange.setStartAfter(wrapper);
        newRange.collapse(true);
        selection.addRange(newRange);

        updateEditorHtmlFromRef(editor);
        detectListItemFormats();
    };
      
    const updateEditorHtmlFromRef = (editor: HTMLDivElement) => {
        if (!newClause.listItems) return;
        const idxStr = Object.keys(listItemEditorsRef.current).find(
            (k) => listItemEditorsRef.current[Number(k)] === editor
        );
        
        if (idxStr !== undefined) {
            const idx = Number(idxStr);
            newClause.listItems[idx].description = editor.innerHTML;
        } else {
            const idx = listItemEditorsRef.current.findIndex((el) => el === editor);
            if (idx >= 0) newClause.listItems[idx].description = editor.innerHTML;
        }
    }
  
    const handleSelectListing = async (listing: PropertyListings) => {
        setSelectedListing(listing);

        setPopoverOpen(false);
        setPopSearchQuery('');
        setValidationErrors([]);
        const formValues = Object.fromEntries(
            form.current.map(f => [f.variable, f.value])
        );
        const appointmentPeriod = fillTemplate(APPOINTMENT_PERIOD, formValues);
        const newDescription = fillTemplate(PAYMENT_TEMPLATE, formValues);
        const filledPaymentTemplate = fillPaymentTemplate(newDescription, formValues);
        const feeTemplate = fillTemplate(FEE_TEMPLATE, formValues);

        const rentalMonths = form.current.find(x => x.variable === 'rentalFeeMonths');
        const monthlyPriceRental = listing.price * Number(rentalMonths?.value || 1);
        const priceToWords = Helper.convertNumberToWords(monthlyPriceRental);
        const remarks = `<strong>RM ${Helper.formatWithDecimal(Number(monthlyPriceRental))} (${priceToWords} Ringgit Malaysia)</strong> the nearest offer acceptable by me/us as the Vendor`
        
        // setSections(prev =>
        //     prev.map(section => {
        //         if (section.id !== 'property') return section;
    
        //         return {
        //             ...section,
        //             clauses: section.clauses.map(clause => {
        //                 if (clause.id !== 'mainContent') return clause;
    
        //                 return {
        //                     ...clause,
        //                     listItems: clause.listItems && clause.listItems.map(item => {
        //                         if (item.id === 'sellingPrice') {
        //                             return {
        //                                 ...item,
        //                                 description: remarks
        //                             };
        //                         } else if(item.id === 'paymentTerms') {
        //                             return {
        //                                 ...item,
        //                                 description: filledPaymentTemplate
        //                             };
        //                         } else {
        //                             return item;
        //                         }
        //                     })
        //                 };
        //             })
        //         };
        //     })
        // );
    }

    const handleInput = () => {
        if (editorRef.current) {
            editorContentRef.current = editorRef.current.innerHTML;
        }
    };

    const updateClauseContent = (
        sectionId: string,
        clauseId: string,
        newContent: string,
        listItemLabel?: string
    ) => {
        setSections(prev =>
            prev.map(section => {
                if (section.id !== sectionId) return section;
    
                return {
                    ...section,
                    clauses: section.clauses.map(clause => {
                        if (clause.id !== clauseId) return clause;
                        if (clause.isList && listItemLabel) {
                            return {
                                ...clause,
                                listItems: clause.listItems?.map(item =>
                                    item.label === listItemLabel
                                        ? { ...item, description: newContent }
                                        : item
                                )
                            };
                        }
                        return {
                            ...clause,
                            content: newContent
                        };
                    })
                };
            })
        );
    };
    
    const updateClauseTitle = (sectionId: string, clauseId: string, title: string) => {
        setSections(prev => prev.map(section => {
            if (section.id === sectionId) {
                return {
                    ...section,
                    clauses: section.clauses.map(clause =>
                        clause.id === clauseId ? { ...clause, title } : clause
                    )
                };
            }
            return section;
        }));
    };

    const deleteClause = (sectionId: string, clauseId: string) => {
        setSections(prev => prev.map(section => {
            if (section.id === sectionId) {
                return {
                    ...section,
                    clauses: section.clauses.filter(clause => clause.id !== clauseId)
                };
            }
            return section;
        }));
    };

    const addOrUpdateClause = () => {
        const finalContent = editorContentRef.current;
        if (editingClause) {
            setSections(prev =>
                prev.map(section => {
                    if (section.id === editingClause.sectionId) {
                        return {
                            ...section,
                            clauses: section.clauses.map(clause =>
                                clause.id === editingClause.clauseId
                                    ? {
                                        ...clause,
                                        ...newClause,
                                        content: finalContent,
                                        id: clause.id,
                                        editable: true,
                                      } as Clause
                                    : clause
                            ),
                        };
                    }
                    return section;
                })
            );
        } else {
            const clause: Clause = {
                ...newClause,
                content: finalContent,
                id: `clause-${Date.now()}`,
                editable: true,
            };
            setSections(prev =>
                prev.map(section => {
                    if (section.id === currentSectionId) {
                        return {
                            ...section,
                            clauses: [...section.clauses, clause],
                        };
                    }
                    return section;
                })
            );
        }
    
        setShowAddClauseDialog(false);
        setNewClause({
            id: '',
            title: '',
            content: '',
            editable: true,
            isSignature: false,
            needsWitness: false,
            witnessFields: {
                name: true,
                nric: true,
                address: false,
                date: true
            }
        });
        editorContentRef.current = '';
        setEditingClause(null);
    };

    const addFieldToClause = () => {
        const selection = window.getSelection();
        const editor = editorRef.current;
        if (!selection || !editor || selection.rangeCount === 0) return;
    
        const range = selection.getRangeAt(0);
        if (!editor.contains(range.startContainer)) return;
    
        let container: Element =
        range.commonAncestorContainer instanceof Element
            ? range.commonAncestorContainer
            : range.commonAncestorContainer.parentElement!;

        // let container = range.commonAncestorContainer;
        // if (container.nodeType === Node.TEXT_NODE) container = container.parentElement!;
    
        let openCode = container.closest('code[data-field-id]:not([data-closed])') as HTMLElement | null;
    
        if (openCode && editor.contains(openCode)) {
            let fieldText = openCode.textContent || '';
            
            const leadingUnderscores = fieldText.match(/^_+/)?.[0] || "__";
            const underscoreCount = Math.max(leadingUnderscores.length, 2);
            
            fieldText = fieldText.replace(/^_+|_+$/g, '').trim();
            fieldText = fieldText.replace(/\s+/g, '_').toUpperCase();
            
            const underscores = '_'.repeat(underscoreCount);
            openCode.textContent = `${underscores}${fieldText}${underscores}`;
            openCode.setAttribute('data-closed', 'true');
            openCode.contentEditable = 'false';
            openCode.style.userSelect = 'none';
            
            const textNode = document.createTextNode('\u00A0');
            openCode.parentNode!.insertBefore(textNode, openCode.nextSibling);
            
            setTimeout(() => {
                const newRange = document.createRange();
                newRange.setStartAfter(openCode);
                newRange.collapse(true);
                selection.removeAllRanges();
                selection.addRange(newRange);
                editor.focus();
                if (editorContentRef.current !== undefined) {
                    editorContentRef.current = editor.innerHTML;
                }
            }, 0);
        } else {
            const codeEl = document.createElement('code');
            const fieldId = `field-${Date.now()}`;
            codeEl.setAttribute('data-field-id', fieldId);
            codeEl.textContent = '__'; 
            codeEl.className = 'bg-gray-200 px-1 py-0.5 rounded text-xs';
    
            if (!range.collapsed) range.deleteContents();
            range.insertNode(codeEl);
    
            const newRange = document.createRange();
            newRange.selectNodeContents(codeEl);
            newRange.collapse(false);
            selection.removeAllRanges();
            selection.addRange(newRange);
            editor.focus();
    
            newClause.fields = [...(newClause.fields || []), { id: fieldId, name: "" }];
            
            if (editorContentRef.current !== undefined) {
                editorContentRef.current = editor.innerHTML;
            }
        }
    };

    const addSection = () => {
        const newSection: Section = {
            id: `section-${Date.now()}`,
            title: 'New Section',
            editable: true,
            clauses: []
        };
        setSections(prev => [...prev, newSection]);
    };

    const updateSectionTitle = (sectionId: string, title: string) => {
        setSections(prev => prev.map(section => 
            section.id === sectionId ? { ...section, title } : section
        ));
    };

    const deleteSection = (sectionId: string) => {
        setSections(prev => prev.filter(section => section.id !== sectionId));
    };

    const toggleSectionCollapse = (sectionId: string) => {
        setSections(prev => prev.map(section => 
            section.id === sectionId ? { ...section, collapsed: !section.collapsed } : section
        ));
    };

    const handleEditClause = (sectionId: string, clause: Clause) => {
        setCurrentSectionId(sectionId);
        setEditingClause({ sectionId, clauseId: clause.id });
        setNewClause({ ...clause });
        setShowAddClauseDialog(true);
        setTimeout(() => {
            if (editorRef.current && clause.content) {
                editorRef.current.innerHTML = clause.content;
                editorContentRef.current = clause.content;
            }
            
            if (clause.listItems) {
                clause.listItems.forEach((item, idx) => {
                    const editor = listItemEditorsRef.current[idx];
                    if (editor && item.description) {
                        editor.innerHTML = item.description;
                    }
                });
            }
        }, 100);
    };

    const searchListings = async (query: string, listingId : (string | null) = null) => {
        try {
            let url = `/me/listings/search`;
            if(listingId) {
                url += `?listingId=${listingId}`;
            } else {
                if (!query.trim() || query.length < 2) {
                    setListings([]);
                    return;
                }
                url += `?search=${encodeURIComponent(query)}`;
            }
            const response = await api.get(url);
            if (!response.data.status) {
                throw new Error(response.data.error);
            }
            if(listingId) {
                handleSelectListing(response.data.listings[0]);
            } else {
                setListings(response.data.listings);
            }
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    };

    const generateDoc = async () => {
        setFieldDialogOpen(false);
        const formValues = Object.fromEntries(
            form.current.map(f => [f.variable, f.value])
        );
        const appointmentPeriod = fillTemplate(APPOINTMENT_PERIOD, formValues);
        const newDescription = fillTemplate(PAYMENT_TEMPLATE, formValues);
        const filledPaymentTemplate = fillPaymentTemplate(newDescription, formValues);
        const feeTemplate = fillTemplate(FEE_TEMPLATE, formValues);
    
        setSections(prev =>
            prev.map(section => {
                if (section.id !== "property") return section;
        
                const updatedClauses = section.clauses.map(clause => {
                    // Update propertyAddress clause based on documentType
                    if (clause.id === "propertyAddress") {
                        return {
                            ...clause,
                            content: `I/We <code class="bg-gray-200 px-1 py-0.5 rounded text-xs">_______${documentType === 'individual' ? 'OWNER' : 'COMPANY'}_NAME_______</code> ${documentType === 'individual' ? 'NRIC': 'Company Registered No'}. <code class="bg-gray-200 px-1 py-0.5 rounded text-xs">_____${documentType === 'individual' ? 'NRIC' : 'COMPANY_REG_NO'}_____</code>, being
    the registered owner/person authorised by the registered owner ("Vendor ") for the property bearing the postal address known as
    <code class="bg-gray-200 px-1 py-0.5 rounded text-xs">__________________${documentType === 'individual' ? 'ADDRESS' : 'COMPANY_ADDRESS'}__________________</code> (" Property") hereby irrevocably appoint <strong>Salt & Light Realty Sdn Bhd
    ("SLRSB")</strong> to secure a Tenant for the Property on the following terms and conditions:-`
                        };
                    }
                    
                    if (clause.id === "mainContent") {
                        const updatedListItems = clause.listItems?.map(item => {
                            if (item.id === "paymentTerms") {
                                return { ...item, description: filledPaymentTemplate };
                            } 
                            if (item.id === "professionalFees") {
                                return { ...item, description: feeTemplate };
                            } 
                            if (item.id === "appointmentPeriod") {
                                return { ...item, description: appointmentPeriod };
                            }
                            return item;
                        });
            
                        return {
                            ...clause,
                            listItems: updatedListItems
                        };
                    }
                    if (clause.isSignature) {
                        return {
                            ...clause,
                            needStamp: documentType === 'company'
                        };
                    }
                    return clause;
                });
        
                return {
                    ...section, 
                    clauses: updatedClauses
                };
            })
        );
        
        setTimeout(() => {
            if (editorRef.current) {
                const mainClause = sections
                    .find(s => s.id === 'property')
                    ?.clauses.find(c => c.id === 'mainContent');
                if (mainClause) {
                    editorRef.current.innerHTML = mainClause.content || '';
                }
            }
    
            sections
                .find(s => s.id === 'property')
                ?.clauses.find(c => c.id === 'mainContent')
                ?.listItems?.forEach((item, idx) => {
                    const el = listItemEditorsRef.current[idx];
                    if (el) el.innerHTML = item.description || '';
                });
        }, 50);
    };

    const toggleFieldInListItemDescription = (idx: number) => {
        const editor = listItemEditorsRef.current[idx];
        if (!editor) return;
    
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
    
        const range = selection.getRangeAt(0);
        if (!editor.contains(range.startContainer)) return;
    
        let container: Element =
        range.commonAncestorContainer instanceof Element
            ? range.commonAncestorContainer
            : range.commonAncestorContainer.parentElement!;
        // let container = range.commonAncestorContainer;
        // if (container.nodeType === Node.TEXT_NODE) container = container.parentElement!;
    
        let openCode = container.closest('code[data-field-id]:not([data-closed])') as HTMLElement | null;
        
        if (openCode && editor.contains(openCode)) {
            let fieldText = openCode.textContent || '';
            
            const leadingUnderscores = fieldText.match(/^_+/)?.[0] || '__';
            const underscoreCount = Math.max(leadingUnderscores.length, 2);
            
            fieldText = fieldText.replace(/^_+|_+$/g, '').trim();
            fieldText = fieldText.replace(/\s+/g, '_').toUpperCase();
            
            const underscores = '_'.repeat(underscoreCount);
            openCode.textContent = `${underscores}${fieldText}${underscores}`;
            openCode.setAttribute('data-closed', 'true');
            openCode.contentEditable = 'false';
            openCode.style.userSelect = 'none';
            
            const textNode = document.createTextNode('\u00A0');
            openCode.parentNode!.insertBefore(textNode, openCode.nextSibling);
            
            setTimeout(() => {
                const newRange = document.createRange();
                newRange.setStartAfter(openCode);
                newRange.collapse(true);
                selection.removeAllRanges();
                selection.addRange(newRange);
                editor.focus();
                
                if (newClause.listItems && newClause.listItems[idx]) {
                    newClause.listItems[idx].description = editor.innerHTML;
                }
            }, 0);
        } else {
            const code = document.createElement('code');
            code.textContent = '__';
            code.className = 'bg-gray-100 px-1 py-0.5 rounded text-xs';
            code.setAttribute('data-field-id', `field-${Date.now()}`);
    
            if (!range.collapsed) range.deleteContents();
            range.insertNode(code);
    
            const newRange = document.createRange();
            newRange.selectNodeContents(code);
            newRange.collapse(false);
            selection.removeAllRanges();
            selection.addRange(newRange);
            editor.focus();
            
            if (newClause.listItems && newClause.listItems[idx]) {
                newClause.listItems[idx].description = editor.innerHTML;
            }
        }
    };
    
    const sendForm = async (e: any) => {
        e.preventDefault();

        try {
            // if(!selectedListing) {
            //     return
            // }
            setloading(true);
            const formValues = Object.fromEntries(
                form.current.map(f => [f.variable, f.value])
            );
            
            const payload: any = {
                listingId: selectedListing ? selectedListing.id : null,
                sections,
                form: formValues,
                signatories: signatoryDetails,
                signingMode,
                ...signingPortalPayload(),
            };
            const response = await api.post(
                `/me/documents/exclusive-authorization-to-rent`,
                payload,
                { headers: { 'Content-Type': 'application/json' } }
            );
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            toast.success('Successfully sent!');
        } catch (err) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setloading(false);
        }
    }
    const positionToolbarNearSelection = (index: number) => {
        const editor = listItemEditorsRef.current[index];
        const toolbar = singleToolbarRef.current;
    
        if (!editor || !toolbar) return;
    
        const selection = window.getSelection();
        
        if (!selection || selection.rangeCount === 0) {
            toolbar.classList.add('hidden');
            toolbar.classList.remove('flex');
            return;
        }
    
        const mousePos = mousePositionRef.current;
        if (!mousePos) return;
    
        toolbar.classList.remove('hidden');
        toolbar.classList.add('flex');
    
        const dialogContent = dialogContentRef.current;
        if (!dialogContent) return;
        
        const scrollTop = dialogContent.scrollTop;
        const toolbarRect = toolbar.getBoundingClientRect();
        
        let left = mousePos.x + 10;
        let top = mousePos.y + scrollTop + 10;
        
        const maxLeft = mousePos.maxX - toolbarRect.width - 10;
        if (left > maxLeft) {
            left = maxLeft;
        }
        if (left < 10) {
            left = 10;
        }
        
        const maxTop = mousePos.maxY + scrollTop - toolbarRect.height - 10;
        if (top > maxTop) {
            top = mousePos.y + scrollTop - toolbarRect.height - 10;
        }
        if (top < scrollTop + 10) {
            top = scrollTop + 10;
        }
    
        toolbar.style.left = `${left}px`;
        toolbar.style.top = `${top}px`;
    };

    const handleEditorBlur = () => {
        setTimeout(() => {
            const selection = window.getSelection();
            const toolbar = singleToolbarRef.current;
            
            if (toolbar && (!selection || selection.rangeCount === 0 || selection.getRangeAt(0).collapsed)) {
                toolbar.classList.add('hidden');
                toolbar.classList.remove('flex');
            }
        }, 200);
    };
 
    const handleMouseUp = (e: any) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const relativeX = e.clientX - rect.left;
        const relativeY = e.clientY - rect.top;
        mousePositionRef.current.x = relativeX;
        mousePositionRef.current.y = relativeY;
        mousePositionRef.current.maxX = rect.width;
        mousePositionRef.current.maxY = rect.height;
    };

    const generatePdf = async () => {
        try {
            setGeneratingPdf(true);
            const formValues = Object.fromEntries(
                form.current.map(f => [f.variable, f.value])
            );
            const payload: any = {
                sections,
                form: formValues,
                signatories: signatoryDetails
            };
            const response = await api.post(
                `/me/listings/documents/exclusive-authorization-to-rent/export`,
                payload,
                { 
                    headers: { 'Content-Type': 'application/json' },
                    responseType: 'blob' 
                }
            );
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Agreement to purchase.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setGeneratingPdf(false);
        }
    }

    // const updateSellingPriceRemarks = () => {
    //     if (!selectedListing) return;
        
    //     const rentalMonths = form.current.find(x => x.variable === 'rentalFeeMonths');
    //     const monthlyPriceRental = selectedListing.price * Number(rentalMonths?.value || 1);
    //     const priceToWords = Helper.convertNumberToWords(monthlyPriceRental);
    //     const remarks = `<strong>RM ${Helper.formatWithDecimal(Number(monthlyPriceRental))} (${priceToWords} Ringgit Malaysia)</strong> the nearest offer acceptable by me/us as the Vendor`;
        
    //     setSections(prev =>
    //         prev.map(section => {
    //             if (section.id !== 'property') return section;
    
    //             return {
    //                 ...section,
    //                 clauses: section.clauses.map(clause => {
    //                     if (clause.id !== 'mainContent') return clause;
    
    //                     return {
    //                         ...clause,
    //                         listItems: clause.listItems && clause.listItems.map(item => {
    //                             if (item.id === 'sellingPrice') {
    //                                 return {
    //                                     ...item,
    //                                     description: remarks
    //                                 };
    //                             } else {
    //                                 return item;
    //                             }
    //                         })
    //                     };
    //                 })
    //             };
    //         })
    //     );
    // };

    useEffect(() => {
        const debounce = setTimeout(() => {
            if (popSearchQuery) {
                searchListings(popSearchQuery);
            }
        }, 300);

        return () => clearTimeout(debounce);
    }, [popSearchQuery]);

    useEffect(() => {
        if(selectedListing || documentType) {
            generateDoc();
        }
    }, [selectedListing, documentType]);
    
    useEffect(() => {
        const handler = () => {
            detectFormats();
            detectListItemFormats();
        };
        document.addEventListener('selectionchange', handler);
        return () => document.removeEventListener('selectionchange', handler);
    }, []);

    useEffect(() => {
        if (showAddClauseDialog) {
            setEditorsInitialized(false);
        }
    }, [showAddClauseDialog, editingClause]);

    useEffect(() => {
        if (!form.current) return;
    
        const formValues = Object.fromEntries(
            form.current.map(f => [f.variable, f.value])
        );

        const inputs: { id: string; variable: string }[] = [
            { id: 'appointmentPeriod', variable: 'appointmentPeriod' },
            { id: 'appointmentCommencementDate', variable: 'appointmentCommencementDate' },
            { id: 'appointmentExpiringDate', variable: 'appointmentExpiringDate' },
            { id: 'securityDepositMonths', variable: 'securityDepositMonths' },
            { id: 'securityDepositAmount', variable: 'securityDepositAmount' },
            { id: 'utilityDepositMonths', variable: 'utilityDepositMonths' },
            { id: 'utilityDepositAmount', variable: 'utilityDepositAmount' },
            { id: 'advanceRentalMonths', variable: 'advanceRentalMonths' },
            { id: 'advanceRentalAmount', variable: 'advanceRentalAmount' },
            { id: 'rentalFeeMonths', variable: 'rentalFeeMonths' },
        ];
        const handlers: { el: HTMLInputElement; fn: (e: Event) => void }[] = [];
    
        inputs.forEach(({ id, variable }) => {
            const el = document.getElementById(id) as HTMLInputElement | null;
            if (!el) return;
    
            const fn = (e: Event) => {
                const target = e.target as HTMLInputElement;
                const newValue = target.type === 'number' || !isNaN(Number(target.value)) 
                    ? Number(target.value) 
                    : target.value;
                const idx = form.current.findIndex(f => f.variable === variable);
                if (idx !== -1) {
                    form.current[idx].value = newValue;
                }
                // updateSellingPriceRemarks();
            };
    
            el.addEventListener('input', fn);
            handlers.push({ el, fn });
    
            if (el.value !== String(formValues[variable] ?? '')) {
                el.value = String(formValues[variable] ?? "");
            }
        });
    
        return () => {
            handlers.forEach(({ el, fn }) => {
                el.removeEventListener('input', fn);
            });
        };
    });

    useEffect(() => {
        const allSignatories: Array<{
            type: 'vendor' | 'purchaser' | 'witness';
            name: string;
            email: string;
            fields: SignatureFields;
            label: string;
            origin?: StampOrigin;
        }> = [];
    
        let vendorCount = 0;
        let purchaserCount = 0;
        let witnessCount = 0;
    
        sections.forEach(section => {
            section.clauses.forEach((clause, clauseIndex) => {
                if (clause.isSignature && clause.signatories && clause.signatories.length > 0) {
                    clause.signatories.forEach((signatory, signerIndex) => {
                        if (signatory.type === 'vendor') {
                            vendorCount++;
                            allSignatories.push({
                                type: signatory.type,
                                name: '',
                                email: '',
                                fields: signatory.fields,
                                origin: { sectionId: section.id, clauseIndex, signerIndex },
                                label: vendorCount > 1 ? `Vendor ${vendorCount}` : 'Vendor'
                            });
                        } else if (signatory.type === 'purchaser') {
                            purchaserCount++;
                            allSignatories.push({
                                type: signatory.type,
                                name: '',
                                email: '',
                                fields: signatory.fields,
                                origin: { sectionId: section.id, clauseIndex, signerIndex },
                                label: purchaserCount > 1 ? `Purchaser ${purchaserCount}` : 'Purchaser'
                            });
                        }
                    });
    
                    if (clause.needsWitness && clause.witnessFields) {
                        witnessCount++;
                        allSignatories.push({
                            type: 'witness',
                            name: '',
                            email: '',
                            fields: clause.witnessFields,
                            label: witnessCount > 1 ? `Witness ${witnessCount}` : 'Witness'
                        });
                    }
                }
            });
        });
    
        if (allSignatories.length > 0) {
            setSignatoryDetails(prev => {
                if (prev.length === allSignatories.length) {
                    return allSignatories.map((sig, idx) => ({
                        ...sig,
                        name: prev[idx]?.name || '',
                        email: prev[idx]?.email || ''
                    }));
                }
                return allSignatories;
            });
        }
    }, [sections]);

    useEffect(() => {
        const listingId = searchParams.get('listingId');
        if(listingId) {
            searchListings('', listingId);
        }
    }, []);

    return (
        <div className="min-h-screen">
            <div className="max-w-5xl mx-auto mt-2">
                <div className='md:flex items-center justify-between mb-2'>
                    <div className="print:hidden w-full md:w-96 flex">
                        <Label className='mr-2'>Ref: </Label>
                        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                variant="outline"
                                size="sm"
                                className={`mb-2 md:mb-0 justify-between text-xs overflow-hidden ${validationErrors.includes('selectedListing') ? 'border-red-400' : ''}`}>
                                    <span className="truncate max-w-[180px] inline-block">
                                        {selectedListing
                                        ? `${selectedListing.listingReferenceNumber} - ${selectedListing.headline}`
                                        : "Search listing..."}
                                    </span>
                                    <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-full p-0" align="start">
                                <Command shouldFilter={false}>
                                    <CommandInput 
                                    placeholder="Search listings..." 
                                    value={popSearchQuery}
                                    onValueChange={setPopSearchQuery}/>
                                    <CommandList>
                                        <CommandEmpty>No listings found.</CommandEmpty>
                                        <CommandGroup>
                                        {
                                            listings && listings.map((listing) => {
                                                return (
                                                    <CommandItem
                                                    key={listing.id}
                                                    value={listing.id.toString()}
                                                    onSelect={() => handleSelectListing(listing)}>
                                                        <Check
                                                        className={cn(
                                                            "mr-2 h-4 w-4",
                                                            selectedListing?.id === listing.id ? 'opacity-100' : 'opacity-0'
                                                        )}/>
                                                        <div className="flex-1">
                                                            <div className="font-medium text-sm">
                                                                {listing.listingReferenceNumber}
                                                            </div>
                                                            <div className="text-xs text-muted-foreground truncate">
                                                                {listing.headline}
                                                            </div>
                                                        </div>
                                                    </CommandItem>
                                                )
                                            })
                                        }
                                        </CommandGroup>
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </div>
                    {/* {
                        selectedListing && ( */}
                            <div className="md:flex justify-end mb-3 gap-2 mt-2">
                                <Button
                                size={'sm'}
                                variant={"outline"}
                                onClick={() => {
                                    setShowPreview(true);
                                }}>
                                    <Eye className="h-4 w-4" />
                                    Preview
                                </Button>
                                <Button onClick={addSection} size={'sm'} className='ml-2'>
                                    <Plus className="h-4 w-4" />
                                    Add Section
                                </Button>
                            </div>
                        {/* )
                    } */}
                </div>
                <Button 
                className="mb-3 text-red-500" 
                size="sm"
                variant="outline"
                onClick={(e) => {
                    e.preventDefault();
                    setFieldDialogOpen(true);
                }}>
                    <AlertCircle className="h-4 w-4 text-red-500" />
                    Fields
                </Button>
                <div className='flex justify-between items-end mb-3'>
                    <div>
                        <Select
                            value={documentType}
                            onValueChange={(value) => setDocumentType(value as ('individual' | 'company'))}
                        >
                            <SelectTrigger className="w-[180px] mb-3">
                                <SelectValue placeholder="Select document type" />
                            </SelectTrigger>

                            <SelectContent>
                                <SelectItem value="individual">Individual</SelectItem>
                                <SelectItem value="company">Company</SelectItem>
                            </SelectContent>
                        </Select>
                        {/* <div className="flex items-center gap-2 mb-3">
                            <Label htmlFor="docusign">Use E-signature?</Label>
                            <Checkbox
                            id="docusign"
                            checked={docusign}
                            onCheckedChange={(checked) => {
                                setDocusign(checked === true);
                            }}/>
                        </div> */}
                    </div>
                    <div className='gap-1 flex'>
                        <Button size={'sm'} onClick={generatePdf} disabled={generatingPdf}>
                            {
                                generatingPdf && (
                                    <Spinner />
                                )
                            }
                            Generate PDF
                        </Button>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                    <div className="flex items-center justify-center">
                        {
                            editingTitle ? (
                                <div className="flex justify-center gap-2 flex-1">
                                    <Input
                                    value={documentTitle}
                                    onChange={(e) => setDocumentTitle(e.target.value)}
                                    className="font-bold text-sm"
                                    // disabled={!selectedListing}
                                    />
                                    <Button size="sm" onClick={() => setEditingTitle(false)}>
                                        <Check className="h-4 w-4" />
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex justify-center items-center gap-2">
                                    <h6 className="text-xl text-center font-bold">{documentTitle}</h6>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setEditingTitle(true)}>
                                        <Edit2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            )
                        }
                    </div>
                </div>
                <div className="space-y-6">
                    {
                        sections.map((section) => (
                            <div key={section.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                                <div className="bg-gray-100 p-4 border-b">
                                    <div className="flex items-center gap-3 flex-1">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => toggleSectionCollapse(section.id)} >
                                            {section.collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                                        </Button>
                                        {
                                            section.editable ? (
                                                <Input
                                                value={section.title}
                                                onChange={(e) => updateSectionTitle(section.id, e.target.value)}
                                                className="font-semibold text-base bg-white"
                                                // disabled={!selectedListing}
                                                />
                                            ) : (
                                                <h2 className="text-lg font-semibold">{section.title}</h2>
                                            )
                                        }
                                        {/* {
                                            selectedListing && ( */}
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => deleteSection(section.id)}>
                                                    <Trash2 className="h-4 w-4 text-red-500" />
                                                </Button>
                                            {/* )
                                        } */}
                                    </div>
                                </div>
                                {!section.collapsed && (
                                    <div className="p-3">
                                        {section.clauses.length === 0 ? (
                                            <div className="text-center py-8 text-gray-600">
                                                <p>No clauses yet. Click "Add Clause" to start.</p>
                                            </div>
                                        ) : (
                                            section.clauses.map((clause, idx) => (
                                                <ClauseItem
                                                key={clause.id}
                                                clause={clause}
                                                sectionId={section.id}
                                                index={idx}
                                                updateClauseTitle={updateClauseTitle}
                                                updateClauseContent={updateClauseContent}
                                                deleteClause={deleteClause}
                                                onEditClause={handleEditClause} 
                                                selectedListing={selectedListing}
                                                documentType={documentType}/>
                                            ))
                                        )}
                                        {/* {
                                            selectedListing && ( */}
                                                <div className='flex justify-center'>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => {
                                                            setCurrentSectionId(section.id);
                                                            setEditingClause(null);
                                                            setNewClause({ id: '', title: '', content: '', editable: true });
                                                            editorContentRef.current = ''; 
                                                            setShowAddClauseDialog(true);
                                                        }}>
                                                        <Plus className="h-4 w-4 mr-1" />
                                                        Add Clause
                                                    </Button>
                                                </div>
                                            {/* )
                                        } */}
                                    </div>
                                )}
                            </div>
                        ))
                    }
                    <div className='mt-5 flex justify-end'>
                        <Button size={'sm'} onClick={(e) => {
                            e.preventDefault();
                            // if(!selectedListing) {
                            //     window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                            //     setValidationErrors(['selectedListing']);
                            //     toast.error('Please complete the listing selection.');
                            //     return
                            // }
                            setSubmitDialogOpen(true);
                        }}>SUBMIT</Button>
                    </div>
                </div>
            </div>
            <Dialog open={showAddClauseDialog} onOpenChange={setShowAddClauseDialog}>
                <DialogContent 
                ref={dialogContentRef}
                onMouseUp={handleMouseUp}
                className="sm:max-w-[600px] max-h-[95%] overflow-scroll ">
                    <DialogHeader>
                        <DialogTitle>{editingClause ? 'Edit Clause' : 'Add Clause'}</DialogTitle>
                    </DialogHeader>
                    <div 
                    ref={singleToolbarRef}
                    className="fixed bg-white px-2 py-1 rounded-lg shadow-xl border gap-1 hidden"
                    style={{ zIndex: 9999 }}
                    onMouseDown={(e) => {
                        e.preventDefault();
                    }}>
                        <Badge
                        className="p-2 cursor-pointer"
                        variant={'outline'}
                        ref={listItemBoldBtnRef}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            applyFormatToListItem('strong', listItemEditorsRef.current[currentListItemIndex.current]);
                            detectListItemFormats(currentListItemIndex.current);
                        }}>
                            <Bold className="h-4 w-4" />
                        </Badge>
                        <Badge
                        ref={listItemItalicBtnRef}
                        variant={'outline'}
                        className="p-2 cursor-pointer"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            applyFormatToListItem('em', listItemEditorsRef.current[currentListItemIndex.current]);
                            detectListItemFormats(currentListItemIndex.current);
                        }}>
                            <Italic className="h-4 w-4" />
                        </Badge>
                        <Badge
                        ref={listItemUnderlineBtnRef}
                        variant={'outline'}
                        className="p-2 cursor-pointer"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            applyFormatToListItem('u', listItemEditorsRef.current[currentListItemIndex.current]);
                            detectListItemFormats(currentListItemIndex.current);
                        }}>
                            <Underline className="h-4 w-4" />
                        </Badge>
                        <div className="mx-1 h-5 w-px bg-gray-300" />
                        <Badge
                        variant="secondary"
                        className="p-2 flex items-center gap-1 cursor-pointer hover:bg-gray-200"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            toggleFieldInListItemDescription(currentListItemIndex.current);
                        }}>
                            <Plus className="h-4 w-4" /> Add Field
                        </Badge>
                    </div>
                    <div className="space-y-4 relative">
                    <div>
                        <Label className="mb-2">Clause Title</Label>
                        <Input
                        value={newClause.title}
                        onChange={(e) => setNewClause({ ...newClause, title: e.target.value })}
                        className={`${validationErrors.includes('newClauseTitle') ? 'border-red-500' : ''}`}
                        // disabled={!selectedListing}
                        />
                        {
                            validationErrors.includes('newClauseTitle') && (
                                <span className="text-red-500 text-xs">Title is required.</span>
                            )
                        }
                    </div>
                    {
                        !newClause.isList && (
                            <div>
                                <Label className="mb-2">Clause Content</Label>
                                <div 
                                ref={toolbarRef} 
                                className="w-full p-2 rounded-xl border bg-white flex items-center gap-1 mb-3">
                                    <Button 
                                    ref={boldBtnRef}
                                    size="sm" 
                                    variant={'outline'} 
                                    className="p-2" 
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        applyFormat("strong");
                                    }}>
                                        <Bold className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                    ref={italicBtnRef}
                                    size="sm" 
                                    variant={'outline'} 
                                    className="p-2" 
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        applyFormat("em");
                                    }}>
                                        <Italic className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                    ref={underlineBtnRef}
                                    size="sm" 
                                    variant={'outline'} 
                                    className="p-2" 
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        applyFormat("u");
                                    }}>
                                        <Underline className="h-4 w-4" />
                                    </Button>
                                    <div className="mx-2 h-5 w-px bg-gray-300" />
                                    <Button
                                    size="sm"
                                    variant="secondary"
                                    className="p-2 flex gap-1"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        addFieldToClause();
                                    }}>
                                        <Plus className="h-4 w-4" />
                                        Add Field
                                    </Button>
                                </div>
                                <div
                                ref={(el) => {
                                    if (el) {
                                        editorRef.current = el;
                                        if (!editorsInitialized) {
                                            el.innerHTML = newClause.content;
                                        }
                                    }
                                }}
                                contentEditable
                                suppressContentEditableWarning
                                onInput={handleInput}
                                onSelect={detectFormats}
                                onFocus={() => setEditorsInitialized(true)}
                                className="min-h-[120px] w-full p-3 rounded-xl border bg-white shadow-sm focus:outline-none 
                                        break-all whitespace-normal"/>
                            </div>
                        )
                    }
                    {
                        !newClause.isSignature && (
                            <div className="flex items-center gap-2">
                                <Checkbox
                                checked={newClause.isList}
                                onCheckedChange={(checked) => {
                                    setNewClause({ 
                                        ...newClause, 
                                        isList: !!checked,
                                        listItems: checked ? newClause.listItems || [{ label: '', description: '' }] : [],
                                        listDisplay: checked ? newClause.listDisplay || 'vertical' : undefined
                                    });
                                }}/>
                                <Label>Is List?</Label>
                            </div>
                        )
                    }
                    {
                        newClause.isList && (
                            <div className="flex items-center gap-4">
                                <Label>Display as:</Label>
                                <div className="flex gap-2">
                                    <Button
                                    size="sm"
                                    variant={newClause.listDisplay === 'vertical' ? 'default' : 'outline'}
                                    onClick={() => setNewClause({ ...newClause, listDisplay: 'vertical' })}
                                    >
                                    Vertical
                                    </Button>
                                    <Button
                                    size="sm"
                                    variant={newClause.listDisplay === 'horizontal' ? 'default' : 'outline'}
                                    onClick={() => setNewClause({ ...newClause, listDisplay: 'horizontal' })}
                                    >
                                    Horizontal
                                    </Button>
                                </div>
                            </div>
                        )
                    }
                    {
                        newClause.isList && (
                            <div className="space-y-4">
                                {
                                    newClause.listItems?.map((item, idx) => (
                                        <div
                                        key={idx}
                                        className="flex flex-col gap-2 w-full mt-5 p-3 border rounded-xl bg-gray-50 shadow-sm relative"
                                        onClick={() => (currentListItemIndex.current = idx)}>
                                            <div className='flex justify-between gap-5 items-center'>
                                                <div className='w-full'>
                                                    <Input
                                                    placeholder="Label"
                                                    value={item.label}
                                                    onChange={(e) => {
                                                        const updatedItems = [...newClause.listItems!];
                                                        updatedItems[idx].label = e.target.value;
                                                        setNewClause({
                                                            ...newClause,
                                                            listItems: updatedItems,
                                                        });
                                                    }}
                                                    className="flex-1 w-full"
                                                    // disabled={!selectedListing}
                                                    />
                                                </div>
                                                <Button
                                                size="sm"
                                                variant="destructive"
                                                onClick={() => {
                                                    setNewClause({
                                                        ...newClause,
                                                        listItems: newClause.listItems!.filter((_, i) => i !== idx),
                                                    });
                                                }}>
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                            
                                            <Label className="font-semibold mt-2">Description</Label>
                                            
                                            <div className="relative">
                                            <div
                                            ref={(el) => {
                                                if (el) {
                                                    listItemEditorsRef.current[idx] = el;
                                                    if (!editorsInitialized) {
                                                        el.innerHTML = item.description || '';
                                                    }
                                                }
                                            }}
                                            contentEditable
                                            suppressContentEditableWarning
                                            className="min-h-[80px] w-full p-2 rounded border bg-white shadow-inner break-all whitespace-normal text-sm"
                                            onFocus={() => { 
                                                currentListItemIndex.current = idx;
                                                setEditorsInitialized(true);
                                            }}
                                            onBlur={handleEditorBlur}
                                            onClick={(e) => { 
                                                currentListItemIndex.current = idx;
                                                positionToolbarNearSelection(idx);
                                                detectListItemFormats(idx);
                                            }}
                                            onMouseUp={(e) => {
                                                currentListItemIndex.current = idx;
                                                positionToolbarNearSelection(idx);
                                            }}
                                            onKeyUp={() => {
                                                positionToolbarNearSelection(idx);
                                                detectListItemFormats(idx);
                                            }}
                                            onInput={(e) => {
                                                const editor = e.currentTarget;
                                                const updatedItems = [...newClause.listItems!];
                                                updatedItems[idx].description = editor.innerHTML;
                                                setNewClause({
                                                    ...newClause,
                                                    listItems: updatedItems,
                                                });
                                            }}/>
                                            </div>
                                        </div>
                                    ))
                                }
                                <Button
                                size="sm"
                                variant="outline"
                                className="mt-2"
                                onClick={() => {
                                    setNewClause({
                                        ...newClause,
                                        listItems: [...(newClause.listItems || []), { label: "", description: "", fields: [] }],
                                    });
                                }}>
                                    <Plus className="h-4 w-4 mr-1" /> Add List Item
                                </Button>
                            </div>
                        )
                    }
                    {
                        !newClause.isList && (
                            <div className="flex items-center gap-6">
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                    checked={newClause.isSignature}
                                    onCheckedChange={(checked) =>
                                        setNewClause({ ...newClause, isSignature: !!checked })
                                    }/>
                                    <Label>Signature Required?</Label>
                                </div>
                                {
                                    newClause.isSignature && (
                                        <div className="flex items-center gap-2">
                                            <Checkbox
                                            checked={newClause.needsWitness}
                                            onCheckedChange={(checked) =>
                                                setNewClause({ ...newClause, needsWitness: !!checked })
                                            }/>
                                            <Label>Require Witness?</Label>
                                        </div>
                                    )
                                }
                                {
                                    newClause.isSignature && (
                                        <div className="flex items-center gap-2">
                                            <Checkbox
                                            checked={newClause.needStamp}
                                            onCheckedChange={(checked) => {
                                                const isChecked = !!checked;
                                                console.log('needStamp changed to:', isChecked);
                                                const alreadyChosen = (newClause.signatories || []).some(s => !!s.fields.stamp);
                                                const updatedSignatories = (newClause.signatories || []).map((signatory, index) => ({
                                                    ...signatory,
                                                    fields: {
                                                        ...signatory.fields,
                                                        stamp: isChecked ? (alreadyChosen ? !!signatory.fields.stamp : index === 0) : false
                                                    }
                                                }));
                                                console.log('Updated signatories:', updatedSignatories);
                                                
                                                setNewClause({ 
                                                    ...newClause, 
                                                    needStamp: isChecked,
                                                    signatories: updatedSignatories
                                                });
                                            }}/>
                                            <Label>Require Stamp?</Label>
                                        </div>
                                    )
                                }
                            </div>
                        )
                    }
                    {
                        newClause.isSignature && (
                            <>
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <Label className="font-semibold">Signatories</Label>
                                        <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                            setNewClause({
                                                ...newClause,
                                                signatories: [
                                                    ...(newClause.signatories || []),
                                                    {
                                                        type: 'vendor',
                                                        fields: {
                                                            name: true,
                                                            nric: true,
                                                            address: true,
                                                            date: true
                                                        }
                                                    }
                                                ]
                                            });
                                        }}>
                                            <Plus className="h-4 w-4 mr-1" />
                                            Add Signatory
                                        </Button>
                                    </div>
                                    {
                                        newClause.signatories?.map((signatory, idx) => (
                                            <div key={idx} className="border rounded-lg p-4 space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <Label className="font-medium">
                                                        Signatory {idx + 1}
                                                    </Label>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => {
                                                            setNewClause({
                                                                ...newClause,
                                                                signatories: newClause.signatories?.filter((_, i) => i !== idx)
                                                            });
                                                        }}
                                                    >
                                                        <Trash2 className="h-4 w-4 text-red-500" />
                                                    </Button>
                                                </div>

                                                <div>
                                                    <Label className="mb-1">Type</Label>
                                                    <Select
                                                        value={signatory.type}
                                                        onValueChange={(value: SignatoryType) => {
                                                            const updated = [...(newClause.signatories || [])];
                                                            updated[idx].type = value;
                                                            setNewClause({ ...newClause, signatories: updated });
                                                        }}
                                                    >
                                                        <SelectTrigger>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="vendor">Vendor</SelectItem>
                                                            <SelectItem value="purchaser">Purchaser</SelectItem>
                                                            <SelectItem value="tenant">Tenant</SelectItem>
                                                            <SelectItem value="landlord">Land Lord</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>

                                                <div>
                                                    <Label className="mb-2">Required Fields</Label>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        {(
                                                            newClause.needStamp
                                                            ? ['name', 'nric', 'address', 'date', 'stamp']
                                                            : ['name', 'nric', 'address', 'date']
                                                        ).map((field) => {
                                                            const key = field as keyof SignatureFields; 

                                                            return (
                                                                <div key={key} className="flex items-center gap-2">
                                                                    <Checkbox
                                                                    checked={signatory.fields[key] || false}
                                                                    onCheckedChange={(checked) => {
                                                                        const updated = [...(newClause.signatories || [])];
                                                                        if (key === 'stamp') {
                                                                            // One stamper per section; unticking clears it.
                                                                            updated.forEach((s, i) => {
                                                                                s.fields.stamp = checked ? i === idx : i === idx ? false : !!s.fields.stamp;
                                                                            });
                                                                        } else {
                                                                            updated[idx].fields[key] = !!checked;
                                                                        }
                                                                        setNewClause({ ...newClause, signatories: updated });
                                                                    }} />
                                                                    <Label className="text-sm">
                                                                    {key.charAt(0).toUpperCase() + key.slice(1)}
                                                                    </Label>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>
                                {
                                    newClause.needsWitness && (
                                        <div className="border rounded-lg p-4 space-y-3">
                                            <Label className="font-medium">Witness Fields</Label>
                                            <div className="grid grid-cols-2 gap-2">
                                                {(['name', 'nric', 'address', 'date'] as (keyof SignatureFields)[]).map((field) => (
                                                    <div key={field} className="flex items-center gap-2">
                                                        <Checkbox
                                                            checked={newClause.witnessFields?.[field] || false}
                                                            onCheckedChange={(checked) =>
                                                                setNewClause({
                                                                    ...newClause,
                                                                    witnessFields: {
                                                                        ...newClause.witnessFields,
                                                                        [field]: !!checked
                                                                    }
                                                                })
                                                            }
                                                        />
                                                        <Label className="text-sm">
                                                            {field.charAt(0).toUpperCase() + field.slice(1)}
                                                        </Label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )
                                }
                            </>
                        )
                    }
                    </div>
                    <DialogFooter className="mt-4 flex justify-end gap-2">
                        <Button
                        variant="outline"
                        onClick={() => {
                            setShowAddClauseDialog(false);
                            setEditingClause(null);
                        }}>
                            Cancel
                        </Button>
                        <Button onClick={addOrUpdateClause}>
                            {editingClause ? 'Save Clause' : 'Add Clause'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog open={showPreview} onOpenChange={setShowPreview}>
                <DialogContent className="sm:max-w-[600px] max-h-[95%] overflow-scroll">
                    <DialogTitle>
                        <VisuallyHidden>Add Clause</VisuallyHidden>
                    </DialogTitle>
                    <div className='grid grid-cols-12 gap-2 items-center'>
                        <div className='col-span-3 flex justify-center'>
                            <img src={`/logo-horizontal.png`} className='w-full max-h-[80px] object-contain'/>
                        </div>
                        <div className='col-span-7'>
                            <h6 className='text-lg font-semibold'>SALT & LIGHT REALTY SDN BHD</h6>
                            <div className='text-xs'>
                                <p>Company No. 202301013601 (1507523-P)</p>
                                <p>NO.1, LEVEL 2, JALAN SETIA UTAMA AT U13/AT, SETIA ALAM, 40170 SHAH ALAM, SELANGOR</p>
                                <p>03-3359 6378</p>
                                <p>sales@saltnlightrealty.com</p>
                            </div>
                        </div>
                        <div className='col-span-2 flex justify-center items-start h-full'>
                            <img src={`/ejen.png`} className='h-[80px]'/>
                        </div>
                    </div>
                    <hr />
                    <h6 className='text-center'>{documentTitle}</h6>
                    {
                        sections.map((section) => (
                            <div key={section.id} className="bg-white rounded-lg overflow-hidden">
                                {
                                    section.title && (
                                        <h6 className="font-semibold">{section.title}</h6>
                                    )
                                }
                                <div className="p-3">
                                    {
                                        section.clauses.map((clause, idx) => {
                                            return (
                                                <React.Fragment key={idx}>
                                                    <h6 className='font-semibold text-sm flex-1 mb-3'>{clause.title}</h6>
                                                    {
                                                        !clause.isList && !clause.isSignature ? 
                                                            <div className='mb-5'>
                                                                <p className='text-xs'
                                                                dangerouslySetInnerHTML={{ __html: replaceInputsWithValues(clause.content, form.current) }}></p>
                                                            </div>
                                                        :   clause.isList ?
                                                            <div className="mb-5 text-xs">
                                                            {
                                                                clause.listItems && clause.listItems?.length > 0 && (
                                                                    <div className="mt-2">
                                                                        {
                                                                            clause.listItems.map((item, idx) => (
                                                                                <div
                                                                                key={idx}
                                                                                className={`${ clause.listDisplay === "horizontal"
                                                                                    ? "grid grid-cols-12 items-start gap-x-3 mb-2 text-sm"
                                                                                    : "flex items-start gap-2 mb-2 text-sm"} mb-3`}>
                                                                                {
                                                                                    clause.listDisplay === "horizontal" ? (
                                                                                        <>
                                                                                            <div className='col-span-1 font-semibold pr-2'>{idx + 1}.</div>
                                                                                            {
                                                                                                item.label === '' ? 
                                                                                                <>
                                                                                                    <div className="col-span-11 text-gray-700 whitespace-normal text-xs"
                                                                                                    dangerouslySetInnerHTML={{ __html: replaceInputsWithValues(item.description, form.current) }}/>
                                                                                                </>
                                                                                                :
                                                                                                item.description === '' ?
                                                                                                <>
                                                                                                    <div className="col-span-11 font-semibold pr-2 text-xs">
                                                                                                        {item.label}
                                                                                                    </div>
                                                                                                </>
                                                                                                : 
                                                                                                <>
                                                                                                    <div className="col-span-3 font-semibold pr-2 text-xs">
                                                                                                        {item.label}
                                                                                                    </div>
                                                                                                    <div className="col-span-8 text-gray-700 whitespace-normal text-xs"
                                                                                                    dangerouslySetInnerHTML={{ __html: replaceInputsWithValues(item.description, form.current) }}/>
                                                                                                </>
                                                                                            }
                                                                                        </>
                                                                                    ) : (
                                                                                        <>
                                                                                            <span className="font-semibold">{idx + 1}.</span>
                                                                                            <div className="flex-1">
                                                                                                <p className="font-semibold italic text-sm">{item.label}</p>
                                                                                                <p className="text-gray-700 text-sm whitespace-normal"
                                                                                                dangerouslySetInnerHTML={{ __html: item.description }}/>
                                                                                            </div>
                                                                                        </>
                                                                                    )
                                                                                }
                                                                                </div>
                                                                            ))
                                                                        }
                                                                    </div>
                                    
                                                                )
                                                            }
                                                            </div>
                                                        :   clause.isSignature && clause.signatories && clause.signatories.length > 0 && (
                                                                <div className="mb-5">
                                                                    <h5 className="font-semibold text-sm mb-2">{clause.title}</h5>
                                                                    <div className={`grid gap-4 grid-cols-2`}>
                                                                        <div className='space-y-4'>
                                                                            {clause.signatories.map((signatory, sigIdx) => {
                                                                                const typeCount = clause.signatories!.filter(s => s.type === signatory.type).length;
                                                                                const indexOfType = clause.signatories!
                                                                                    .slice(0, sigIdx + 1)
                                                                                    .filter(s => s.type === signatory.type).length;
                                                                                
                                                                                const signatoryLabel = typeCount > 1 
                                                                                    ? `${signatory.type.toUpperCase()} ${indexOfType}`
                                                                                    : signatory.type.toUpperCase();
                
                                                                                return (
                                                                                    <div key={sigIdx} className="rounded-lg p-3">
                                                                                        <div className="text-gray-800 font-semibold text-xs mb-2">
                                                                                            {signatoryLabel}
                                                                                        </div>
                                                                                        <div className="text-gray-600 text-xs mb-1">Sign here</div>
                                                                                        <div className="border border-gray-300 rounded-md h-[120px] mb-3 flex justify-center items-center">
                                                                                            <code className='bg-gray-200 px-1 py-0.5 rounded text-xs'>
                                                                                                {`_____${signatory.type.toUpperCase()}_${sigIdx + 1}_SIGNATURE_____`}
                                                                                            </code>
                                                                                        </div>
                                                                                        <div className="space-y-2 text-sm mt-5">
                                                                                            {signatory.fields.name && (
                                                                                                <div className="flex items-center">
                                                                                                    <span className="text-gray-600 text-xs">Name:</span>
                                                                                                    <code className='bg-gray-200 px-1 py-0.5 rounded text-xs ml-2'>
                                                                                                        {`__${signatory.type.toUpperCase()}_${sigIdx + 1}_NAME__`}
                                                                                                    </code>
                                                                                                </div>
                                                                                            )}
                                                                                            {signatory.fields.nric && (
                                                                                                <div className="flex items-center">
                                                                                                    <span className="text-gray-600 text-xs">NRIC:</span>
                                                                                                    <code className='bg-gray-200 px-1 py-0.5 rounded text-xs ml-2'>
                                                                                                        {`__${signatory.type.toUpperCase()}_${sigIdx + 1}_NRIC__`}
                                                                                                    </code>
                                                                                                </div>
                                                                                            )}
                                                                                            {signatory.fields.address && (
                                                                                                <div className="flex items-center">
                                                                                                    <span className="text-gray-600 text-xs">Address:</span>
                                                                                                    <code className='bg-gray-200 px-1 py-0.5 rounded text-xs ml-2'>
                                                                                                        {`_____${signatory.type.toUpperCase()}_${sigIdx + 1}_ADDRESS_____`}
                                                                                                    </code>
                                                                                                </div>
                                                                                            )}
                                                                                            {signatory.fields.date && (
                                                                                                <div className="flex items-center">
                                                                                                    <span className="text-gray-600 text-xs">Date:</span>
                                                                                                    <code className='bg-gray-200 px-1 py-0.5 rounded text-xs ml-2'>
                                                                                                        {`__${signatory.type.toUpperCase()}_${sigIdx + 1}_DATE__`}
                                                                                                    </code>
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                            {
                                                                                clause.needStamp && (
                                                                                    <div className="rounded-lg p-3">
                                                                                        <div className="space-y-2 text-sm">
                                                                                            <div>
                                                                                                <span className="text-gray-600 text-xs">Company Stamp:</span>
                                                                                                <div className='border border-gray-300 rounded-md h-[120px] flex items-center justify-center'>
                                                                                                    <code className='bg-gray-200 px-1 py-0.5 rounded text-xs ml-2'>
                                                                                                        {`__COMPANY_STAMP__`}
                                                                                                    </code>
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                )
                                                                            }
                                                                        </div>
                                                                        {clause.needsWitness && (
                                                                            <div className="rounded-lg p-3">
                                                                                <div className="text-gray-800 font-semibold text-xs mb-2">WITNESS</div>
                                                                                <div className="text-gray-600 text-xs mb-1">Witness signature</div>
                                                                                <div className="border border-gray-300 rounded-md h-[120px] mb-3 flex justify-center items-center bg-white">
                                                                                    <code className='bg-gray-200 px-1 py-0.5 rounded text-xs'>
                                                                                        _____WITNESS_SIGNATURE_____
                                                                                    </code>
                                                                                </div>
                                                                                <div className="space-y-2 text-sm mt-5">
                                                                                    {clause.witnessFields?.name && (
                                                                                        <div className="flex items-center">
                                                                                            <span className="text-gray-600 text-xs">Name:</span>
                                                                                            <code className='bg-gray-200 px-1 py-0.5 rounded text-xs ml-2'>
                                                                                                __WITNESS_NAME__
                                                                                            </code>
                                                                                        </div>
                                                                                    )}
                                                                                    {clause.witnessFields?.nric && (
                                                                                        <div className="flex items-center">
                                                                                            <span className="text-gray-600 text-xs">NRIC:</span>
                                                                                            <code className='bg-gray-200 px-1 py-0.5 rounded text-xs ml-2'>
                                                                                                __WITNESS_NRIC__
                                                                                            </code>
                                                                                        </div>
                                                                                    )}
                                                                                    {clause.witnessFields?.address && (
                                                                                        <div className="flex items-center">
                                                                                            <span className="text-gray-600 text-xs">Address:</span>
                                                                                            <code className='bg-gray-200 px-1 py-0.5 rounded text-xs ml-2'>
                                                                                                _____WITNESS_ADDRESS_____
                                                                                            </code>
                                                                                        </div>
                                                                                    )}
                                                                                    {clause.witnessFields?.date && (
                                                                                        <div className="flex items-center">
                                                                                            <span className="text-gray-600 text-xs">Date:</span>
                                                                                            <code className='bg-gray-200 px-1 py-0.5 rounded text-xs ml-2'>
                                                                                                __WITNESS_DATE__
                                                                                            </code>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )
                                                    }
                                                </React.Fragment>
                                            )
                                        })
                                    }
                                </div>
                            </div>
                        ))
                    }
                </DialogContent>
            </Dialog>
            <Dialog open={fieldDialogOpen} onOpenChange={setFieldDialogOpen}>
                <DialogContent>
                    <DialogTitle>Required Fields</DialogTitle>
                    {
                        form.current.map((field, idx) => {
                            if (!field.required) return null;

                            return (
                                <div className="flex items-center gap-2" key={field.variable}>
                                    <Label className="w-40">{field.label}:</Label>
                                    <Input
                                    className="flex-1 text-sm text-end"
                                    type="text"
                                    defaultValue={field.value}
                                    onChange={(e) => {
                                        form.current[idx].value = e.target.value;
                                    }}/>
                                </div>
                            );
                        })
                    }
                    <div className='flex justify-end mt-3'>
                        <Button 
                        size={'sm'}
                        onClick={generateDoc}>Apply</Button>
                    </div>
                </DialogContent>
            </Dialog>
            <SigningDetailsDialog
                onStampChange={(index, on) => {
                    const origin = signatoryDetails[index]?.origin;
                    if (origin) setSections(prev => applyClauseStamp(prev, origin, on));
                }}
                open={submitDialogOpen}
                onOpenChange={setSubmitDialogOpen}
                signatoryDetails={signatoryDetails}
                setSignatoryDetails={setSignatoryDetails}
                signingMode={signingMode}
                setSigningMode={setSigningMode}
                sending={loading}
                onSubmit={sendForm}
            />
        </div>
    );
};

export default ExclusiveAuthorizationToRent;
